import { MatTooltipModule } from '@angular/material/tooltip';
import { CdkMenuModule } from '@angular/cdk/menu';
import { NgClass } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import moment from 'moment';
import { TourMatMenuModule } from 'ngx-ui-tour-md-menu';
import { ApiS3Service } from 'src/app/services/api-s3.service';
import { ApiUsuarioService } from 'src/app/services/api-usuario.service';
import { ExtensaoService } from 'src/app/services/extensao.service';
import { PlatformService } from 'src/app/services/platform.service';
import { UtilsService } from 'src/app/services/utils.service';
import { menuCotacao_new } from 'src/app/utils/menus-apice';
import { MenuNovaCotacaoModule } from '../menu-nova-cotacao/menu-nova-cotacao.module';
import { NotificacoesComponent } from '../notificacoes/notificacoes.component';
import { UserData } from './header';
import { headerIcons } from './header.icons';
import { HeaderService } from './header.service';
import { MenuSeguradorasComponent } from './menu-seguradoras/menu-seguradoras.component';
import { MenuUserComponent } from './menu-user/menu-user.component';
import { NotificacoesServiceService } from '../notificacoes/notificacoes-service.service';
import { Notificacao } from '../notificacoes/notificacoes.type';
import { finalize, map } from 'rxjs';
import { ShowIfAuthorizedModule } from 'src/app/directives/show-if-authorized/show-if-authorized.module';
import { HubAuthCacheService } from 'src/app/pages/to-segurado/services/hub-auth-cache.service';
import { AppService } from 'src/app/services/app.service';

@Component({
  selector: 'app-header',
  imports: [
    NgIcon,
    NgClass,
    CdkMenuModule,
    MatMenuModule,
    MenuUserComponent,
    MenuNovaCotacaoModule,
    MenuSeguradorasComponent,
    NotificacoesComponent,
    MatTooltipModule,
    TourMatMenuModule,
    ShowIfAuthorizedModule
  ],
  providers: [provideIcons(headerIcons)],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  private router = inject(Router);
  private apiS3 = inject(ApiS3Service);
  protected apiUsuario = inject(ApiUsuarioService);
  private extensaoService = inject(ExtensaoService);
  protected platformService = inject(PlatformService);
  protected utilsService = inject(UtilsService);
  protected headerService = inject(HeaderService);
  private notificacoesService = inject(NotificacoesServiceService);
  private hubAuthCacheService = inject(HubAuthCacheService);
  protected appService = inject(AppService);

  dadosAssinatura = signal<any>(null);
  vencimento = signal<any>(null);
  showBoleto = signal<any>(false);
  showBannerVencimento = signal<boolean>(false);

  billetDescriptionMap = signal<any>(null);

  faturaLabel = signal<string>("Imprimir Fatura");

  protected dadosUsuario: UserData;

  public initialsName: string = '';

  protected avatar!: any;
  protected isAlternativeAvatar = false;

  protected menusCotacao = menuCotacao_new;

  protected statusExtensao = signal<boolean>(false);

  private destroyRef = inject(DestroyRef);

  notificacoes = signal<Notificacao[]>([]);
  notificacoesNaoLidas = signal<number>(0);
  notificacoesLidas = signal<string[]>([]);

  ngOnInit(): void {
    this.loadAvatar();
    this.setUserData();
    this.checkStatusExtensao();
    this.setIniciaisNomeLogo();
    this.buscaAssinatura();
    this.buscaNotificacoes();
  }

  private buscaNotificacoes = (): void => {
    const notificacoesLidas =
      JSON.parse(localStorage.getItem('notificacoesLidas')) || [];
    this.notificacoesLidas.set(notificacoesLidas);

    this.notificacoesService
      .buscaTodasNotificacoes()
      .pipe(
        map((notificacoes) => {
          const senhaNotif = this.gerarNotificacaoSenhaExpirando();
          const todas = senhaNotif ? [senhaNotif, ...notificacoes] : notificacoes;
          const naoLidas = todas.filter(
            (notificacao) => !notificacoesLidas.includes(notificacao.id)
          );
          this.notificacoesNaoLidas.set(naoLidas.length);
          return todas;
        }),
        finalize(() => {})
      )
      .subscribe({
        next: (notificacoes) => {
          this.notificacoes.set(notificacoes);
        },
      });
  };

  /**
   * Gera dinamicamente (no front) uma notificação de expiração de senha usando
   * o campo diasParaExpirarSenha entregue pelo backend na sessão. Assim evitamos
   * ter que criar/deletar registros na tabela de notificações a cada dia.
   */
  private gerarNotificacaoSenhaExpirando(): Notificacao | null {
    const credenciais: any = this.apiUsuario.getCredenciais?.() || {};
    const dias = credenciais.diasParaExpirarSenha;
    if (typeof dias !== 'number') return null;

    // Gatilhos definidos na política: 15, 7 e 1 dia antes do vencimento
    const gatilho = dias <= 1 ? 1 : dias <= 7 ? 7 : dias <= 15 ? 15 : null;
    if (gatilho === null || dias < 0) return null;

    const diasTxt = dias <= 0 ? 'hoje' : `em ${dias} ${dias === 1 ? 'dia' : 'dias'}`;

    return {
      id: `senha-expira-${gatilho}`,
      titulo: 'Sua senha expirará em breve',
      descricao: {
        texto: `Para manter a segurança da sua conta, sua senha expirará ${diasTxt}. Recomendamos realizar a atualização antecipadamente para evitar interrupções no acesso ao sistema.`,
        img: '',
        link: '/perfil-usuario?alterarSenha=true',
        tipo: 3,
      },
      tipo: 3,
      dataIni: new Date().toISOString(),
      dataFim: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tempoCriacao: '',
      categoria: { txt: 'Alerta', icon: 'comment-alert-outline' },
    };
  }

  marcarComoLida(notificacaoId: string) {
    const notificacoesLidas =
      JSON.parse(localStorage.getItem('notificacoesLidas')) || [];
    if (!notificacoesLidas.includes(notificacaoId)) {
      notificacoesLidas.push(notificacaoId);
      localStorage.setItem(
        'notificacoesLidas',
        JSON.stringify(notificacoesLidas)
      );
      this.buscaNotificacoes();
    }
  }

  private checkStatusExtensao = () => {
    this.statusExtensao = this.extensaoService.extensaoAtiva;
  };

  private setUserData = () => {
    const {
      admin,
      corretora,
      email,
      grupoPermissoesUsuario,
      nome,
      root,
    }: UserData = this.apiUsuario.getCredenciais();

    this.dadosUsuario = {
      nome,
      email,
      admin,
      root,
      corretora,
      grupoPermissoesUsuario,
    };
  };

  private loadAvatar = async () => {
    if (!this.apiUsuario.avatar) {
      this.apiUsuario.avatar = await this.apiS3.loadAvatarUsuarioLogado();
    }
  };
  public logout = () => {
    this.apiS3.avatarUsuarioLogado = null;
    this.apiUsuario.avatar = null;
    this.router.navigateByUrl('/login');
    localStorage.setItem('pathRefer', '');
    localStorage.removeItem('bannerDueDate');
  };

  public setAlternativeAvatar = () => {
    this.isAlternativeAvatar = true;
  };

  public instalarExtensao = () => {
    if (
      this.platformService.deviceService.browser == 'Chrome' ||
      this.platformService.deviceService.browser == 'MS-Edge-Chromium'
    ) {
      window.open(
        'https://chromewebstore.google.com/detail/aggilizador/ebmkefigmmcbigompbbacndedlapmdcc',
        '_blank'
      );
    } else {
      this.utilsService.alertCustom({
        txt: 'Extensão disponível apenas para o navegador Google Chrome e o Novo Microsoft Edge!',
        alertType: 'ERROR',
      });
    }
  };

  private setIniciaisNomeLogo = () => {
    const arrayName = this.dadosUsuario.nome.split(' ');
    const initialFirstName = arrayName[0]?.slice(0, 1);
    const initialLastName = arrayName[1]?.slice(0, 1);

    this.initialsName = `${initialFirstName}${initialLastName ?? ''}`;
  };

  async buscaAssinatura() {
    if (!this.apiUsuario.isUserAdmin()) {
      this.showBannerVencimento.set(false);
      return;
    }
    this.dadosAssinatura.set(this.apiUsuario.getCredenciais());
    this.vencimento.set(
      moment(this.dadosAssinatura().licenca).format('DD/MM/YYYY')
    );
    let existe = await this.verificarUrlExiste();
    if (existe) {
      this.showBoleto.set(true);
    } else {
      this.faturaLabel.set('Acessar Central');
    }
    this.setBilletDescriptions();
  }

  private setBilletDescriptions(): void {
    const bannerDueDateStorage = localStorage.getItem('bannerDueDate');
    if (bannerDueDateStorage) {
      const { hideUntil } = JSON.parse(bannerDueDateStorage);
      if (moment().isBefore(moment(hideUntil, 'YYYY-MM-DD'))) {
        this.showBannerVencimento.set(false);
        return;
      }
    }
    const dueDate = moment(this.vencimento(), 'DD/MM/YYYY', true);

    const today = moment().startOf('day');

    if (!dueDate.isValid()) {
      this.showBannerVencimento.set(false);
      return;
    }

    const daysUntilDue = dueDate.startOf('day').diff(today, 'days');

    if (daysUntilDue > 10) {
      this.showBannerVencimento.set(false);
      return;
    }

    this.showBannerVencimento.set(true);

    const { description, icon, status, color } =
      this.getBilletStatusInfo(daysUntilDue);

    this.billetDescriptionMap.set({
      description,
      icon,
      color,
      status,
      daysUntilDue,
      dueDate: dueDate.format('DD/MM/YYYY'),
    });
  }

  private getBilletStatusInfo(daysUntilDue: number): {
    description: string;
    icon: string;
    status: 'ok' | 'warning' | 'overdue' | 'today';
    color: string;
  } {
    const pluralize = (n: number) => (Math.abs(n) === 1 ? 'dia' : 'dias');

    const statusConfig = {
      overdue: {
        condition: daysUntilDue < 0,
        description:
          'A sua fatura está vencida, efetue o pagamento e evite bloqueios.',
        icon: 'heroExclamationTriangleSolid',
        status: 'overdue' as const,
        color: '#a40013',
      },
      today: {
        condition: daysUntilDue === 0,
        description: 'A sua fatura vence hoje.',
        icon: 'heroClockSolid',
        status: 'today' as const,
        color: '#aa7a06',
      },
      warning: {
        condition: daysUntilDue <= 3 && daysUntilDue > 0,
        description: `Faltam ${daysUntilDue} ${pluralize(
          daysUntilDue
        )} para o vencimento da sua fatura.`,
        icon: 'heroClockSolid',
        status: 'warning' as const,
        color: '#aa7a06',
      },
      ok: {
        condition: daysUntilDue > 3,
        description: `Faltam ${daysUntilDue} ${pluralize(
          daysUntilDue
        )} para o vencimento da fatura.`,
        icon: 'heroInformationCircleSolid',
        status: 'ok' as const,
        color: '#1471c5',
      },
    };

    const matchedStatus = Object.values(statusConfig).find(
      (config) => config.condition
    );

    return matchedStatus || statusConfig.ok;
  }

  async imprimirFatura() {
    try {
      let url = this.showBoleto()
        ? `https://ngapi.agger.com.br/Billet/Customer/${
            this.dadosAssinatura().assinaturaId
          }`
        : this.apiUsuario.urlCentral;

      window.open(url, '_blank');
    } catch (error) {
      this.utilsService.alertCustom({
        txt:
          'Erro ao gerar boleto: ' + (error.message || 'Verifique sua conexão'),
        duration: 5000,
        alertType: 'ERROR',
      });
    }
  }

  async verificarUrlExiste(): Promise<boolean> {
    try {
      let url = `https://ngapi.agger.com.br/Billet/Customer/${
        this.dadosAssinatura().assinaturaId
      }`;
      const response = await fetch(url, {
        method: 'GET',
      });
      return response.ok;
    } catch (error) {
      console.error('Erro ao verificar URL:', error);
      return false;
    }
  }

  getLicenca() {
    if (this.dadosAssinatura()) {
      let data = this.dadosAssinatura().licenca;
      let dataFormatada = moment(data).format('DD/MM/YYYY');
      return dataFormatada;
    }
    return '';
  }

  getDadosAssinatura() {
    return JSON.stringify(this.dadosAssinatura());
  }

  closeBanner() {
    this.showBannerVencimento.set(false);
    const status = this.billetDescriptionMap()?.status;
    const dueDate = moment(this.vencimento(), 'DD/MM/YYYY', true);

    let hideUntil: moment.Moment;

    if (status === 'warning' || status === 'overdue' || status === 'today') {
      hideUntil = moment().add(1, 'day');
    } else {
      // status === 'ok'
      hideUntil = dueDate.clone().subtract(3, 'days');
    }

    localStorage.setItem(
      'bannerDueDate',
      JSON.stringify({ hideUntil: hideUntil.format('YYYY-MM-DD') })
    );
  }
}
